import PropTypes from "prop-types"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"

import ReviewsChart from "areas/info/reviews-chart"
import ShopNowGrid from "components/shop-now-grid"
import Button from "components/ui/button"
import Image from "components/ui/image"
import Picture from "components/ui/picture"
import { IS_GE_CHECKOUT, jewlr_t } from "helpers/application"

const StatCard = ({ title, value }) => (
  <div className="flex-1 px-6">
    <p className="text-32 desktop:text-42 leading-40 desktop:leading-50 text-center uppercase">
      {value}
    </p>
    <p className="text-16 leading-19 text-center">{title}</p>
  </div>
)

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
}

const statData = [
  [
    {
      title: "Founded",
      value: "2009",
    },
    {
      title: "Happy Customers",
      value: "1.5M",
    },
    {
      title: "Off Retail",
      value: "$AVE",
    },
  ],
  [
    {
      title: "Styles",
      value: "6000+",
    },
    {
      title: "Visualization",
      value: "3D",
    },
    {
      title: "Pushy Salespeople",
      value: "0",
    },
  ],
  [
    {
      title: "Day Returns",
      value: IS_GE_CHECKOUT() ? "30" : "99",
    },
    {
      title: "Shipping",
      value: "FREE",
    },
    {
      title: "1-Year Warranty",
      value: "FREE",
    },
  ],
]

const Stats = () => (
  <div>
    {statData.map((row, r) => (
      <div className="mt-40 flex justify-between" key={r}>
        {row.map((item) => (
          <StatCard key={item.title} {...item} />
        ))}
      </div>
    ))}
  </div>
)

const AboutUs = () => {
  return (
    <>
      <Helmet>
        <title>About Jewlr</title>
      </Helmet>
      <div style={{ background: "#ebf3f5" }}>
        <div className="contained-wide">
          <div className="desktop:px-0 tablet:py-40 p-32">
            <h1 className="text-32 leading-36 mb-8 text-center font-bold">
              About Jewlr
            </h1>
            <p className="text-20 leading-24 text-center font-medium">
              This is what happens when a group of software engineers
              <br className="tablet:block hidden" /> team up with designers and{" "}
              {jewlr_t("terms.jeweler")}s.
            </p>
          </div>
          <Picture
            alt="Jewlr Team Photo"
            fallback={require("images/about-us/team-photo-desktop.jpg")}
            srcSet={[
              {
                height: "360",
                media: "(max-width: 767px)",
                sizes: "100vw",
                srcSet: `${require("images/about-us/team-photo-mobile.jpg")} 767w`,
                width: "767",
              },
              {
                height: "433",
                media: "(min-width: 768px) and (max-width: 1023px)",
                sizes: "100vw",
                srcSet: `
              ${require("images/about-us/team-photo-tablet.jpg")} 1020w,
              ${require("images/about-us/team-photo-tablet-2x.jpg")} 2040w
              `,
                width: "1020",
              },
              {
                height: "445",
                media: "(min-width: 1024px)",
                sizes: "1920px",
                srcSet: `
              ${require("images/about-us/team-photo-desktop.jpg")} 1920w,
              ${require("images/about-us/team-photo-desktop-2x.jpg")} 3840w
              `,
                width: "1920",
              },
            ]}
          />
        </div>
      </div>
      <div
        className="contained px-16 py-48"
        style={{ "--desktop-width": "711px" }}
      >
        <h2 className="text-26 leading-30 mb-16 text-center font-medium capitalize">
          Our Mission
        </h2>
        <p className="text-20 leading-20 mb-16 text-center font-bold">
          {jewlr_t("Jewelry")} made just for you.
        </p>
        <p className="text-16 leading-22 desktop:w-(--desktop-width) mx-auto mb-16 w-full text-center font-light tracking-tight">
          Jewlr is a new kind of {jewlr_t("jewelry")} company designed to help
          people create and purchase custom-made {jewlr_t("terms.personalized")}{" "}
          {jewlr_t("jewelry")} online, wherever they are, at below retail
          prices. We have removed all obstacles and risks.
        </p>
        <p className="text-16 leading-22 desktop:w-(--desktop-width) mx-auto mb-16 w-full text-center font-light tracking-tight">
          Our mission is to provide an awesome online {jewlr_t("jewelry")}
          -buying experience that is fast, easy, affordable &#8211; and fun!
        </p>
      </div>
      <div style={{ background: "#515568" }}>
        <div className="contained px-16 pb-56 pt-48 text-white">
          <h2 className="text-26 leading-30 mb-16 text-center font-medium capitalize">
            Our Story
          </h2>
          <p className="text-20 leading-20 mb-16 text-center font-bold">
            We knew we could do better.
          </p>
          <p
            className="text-16 leading-22 desktop:w-(--desktop-width) mx-auto mb-16 w-full text-center font-light tracking-tight"
            style={{ "--desktop-width": "880px" }}
          >
            We have all experienced the challenges of buying{" "}
            {jewlr_t("jewelry")}. Pushy salespeople, limited choice and high
            prices. We set out to create a truly unique online experience where
            you choose, {jewlr_t("terms.personalize")}, and{" "}
            {jewlr_t("terms.customize")} your unique item and we make it for you
            in just a few days – and at an incredible price.
          </p>
          <Stats />
        </div>
      </div>
      <div className="contained px-16 py-48">
        <h2 className="text-26 leading-30 mb-16 text-center font-medium capitalize">
          Lowest Prices
        </h2>
        <p className="text-20 leading-20 mb-16 text-center font-bold">
          Designed & Made at Jewlr.
        </p>
        <p
          className="text-16 leading-22 desktop:w-(--desktop-width) mx-auto mb-16 w-full text-center font-light tracking-tight"
          style={{ "--desktop-width": "820px" }}
        >
          By making the {jewlr_t("jewelry")} ourselves and selling directly to
          you online, we are able to offer the lowest prices. No inventory means
          that your {jewlr_t("jewelry")} is created just for you within a few
          days. We ship directly to you which means you receive your{" "}
          {jewlr_t("jewelry")} quickly.
        </p>
        <p className="text-18 mb-56 mt-80 text-center font-bold tracking-tight">
          Traditional Retail
        </p>
        <Picture
          alt="Retail consumer model"
          fallback={require("images/about-us/trad-consumer-model.svg")}
          srcSet={[
            {
              height: "105",
              media: "(max-width: 767px)",
              sizes: "100vw",
              srcSet: `${require("images/about-us/trad-consumer-model-m.svg")} 335w`,
              width: "335",
            },
            {
              height: "145",
              media: "(min-width: 768px)",
              sizes: "860px",
              srcSet: `${require("images/about-us/trad-consumer-model.svg")} 860w`,
              width: "860",
            },
          ]}
        />
        <p className="text-18 mb-56 mt-80 text-center font-bold tracking-tight">
          Jewlr
        </p>
        <Picture
          alt="Jewlr consumer model"
          fallback={require("images/about-us/jewlr-consumer-model.svg")}
          srcSet={[
            {
              height: "110",
              media: "(max-width: 767px)",
              sizes: "100vw",
              srcSet: `${require("images/about-us/jewlr-consumer-model-m.svg")} 369w`,
              width: "369",
            },
            {
              height: "145",
              media: "(min-width: 768px)",
              sizes: "872px",
              srcSet: `${require("images/about-us/jewlr-consumer-model.svg")} 872w`,
              width: "872",
            },
          ]}
        />
      </div>
      <div className="bg-grey-5 px-16 py-48">
        <div className="contained">
          <h2 className="text-26 leading-30 mb-16 text-center font-medium capitalize">
            Made in North America
          </h2>
          <p className="text-20 leading-20 mb-16 text-center font-bold">
            Quality made. Ethically sourced.
          </p>
          <p
            className="text-16 leading-22 desktop:w-(--desktop-width) mx-auto mb-16 w-full text-center font-light tracking-tight"
            style={{ "--desktop-width": "711px" }}
          >
            Your {jewlr_t("jewelry")} is custom-made in our own facilities in
            North America, which means we control the quality and guarantee that
            materials are ethically sourced. Our team of {jewlr_t("jewelry")}{" "}
            designers and craftsmen create your one-of-a-kind{" "}
            {jewlr_t("jewelry")} using your choice of solid precious metals and
            genuine or simulated stones.
          </p>
          <div className="m-auto flex justify-center">
            <Image
              alt="US and Canada Flags"
              height="124"
              src={require("images/about-us/us-can.png")}
              width="180"
            />
          </div>
          <p className="text-14 leading-22 text-center tracking-tight">
            MADE IN USA AND CANADA
          </p>
        </div>
      </div>
      <div className="contained px-16 py-48">
        <h2 className="text-26 leading-30 mb-16 text-center font-medium capitalize">
          Reviews
        </h2>
        <p className="text-20 leading-20 mb-16 text-center font-bold">
          Our Customers Love Us!
        </p>
        <p className="text-16 leading-22 mb-16 text-center font-light tracking-tight">
          (but don&apos;t just take our word for it.....)
        </p>
        <ReviewsChart />
        <div className="flex justify-center">
          <Button asChild variant="secondary">
            <Link data-lc="about-us" to="/reviews">
              Read Reviews
            </Link>
          </Button>
        </div>
      </div>
      <ShopNowGrid />
    </>
  )
}

export default AboutUs
